import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import * as d3Force from "d3-force";
import * as d3Sel from "d3-selection";
import * as d3Drag from "d3-drag";
import * as d3Zoom from "d3-zoom";

// BubbleMap — D3 force-directed graph with radial concentric layout.
//
// graph = { nodes: [{ id, type, label, ... }], links: [{ source, target }] }
//
// 4-layer hierarchy laid out as concentric rings (org at center):
//   - org:     filled rounded square, anchored dead center
//   - member:  filled circle, ring r=170, opacity 0.55 for juniors
//   - project: hollow circle, ring r=320, hover shows industry + status
//   - tech:    rotated square (diamond), ring r=460
//
// Faint dashed rings render behind the nodes as layer guides. Edges are
// curved quadratic SVG paths to reduce visual overlap.
//
// Zoom + pan via d3-zoom: wheel zooms, background drag pans, scaleExtent
// [0.3, 3]. Node drag still works — zoom filter blocks gestures that
// start on a .node element so d3-drag owns those.

const Wrap = styled.div`
  position: relative;
  width: 100%;
`;

const Tooltip = styled.div`
  position: fixed;
  pointer-events: none;
  z-index: 50;
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  color: var(--nd-text-display);
  background: var(--nd-surface);
  border: 1px solid var(--nd-border-visible);
  border-radius: 6px;
  padding: 8px 10px;
  transform: translate(-50%, calc(-100% - 12px));
  white-space: nowrap;
`;

const ZoomBar = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  z-index: 10;
`;

const ZoomBtn = styled.button`
  width: 32px;
  height: 32px;
  background: var(--nd-surface);
  border: 1px solid var(--nd-border-visible);
  border-radius: 4px;
  color: var(--nd-text-secondary);
  font-family: 'Space Mono', monospace;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  outline: none;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    color: var(--nd-text-display);
    border-color: var(--nd-text-display);
  }
`;

const ZoomLevel = styled.div`
  font-family: 'Space Mono', monospace;
  font-size: 9px;
  letter-spacing: 0.06em;
  color: var(--nd-text-disabled);
  text-align: center;
  padding: 2px 0;
`;

// Radial layout: each node type is pinned to a concentric ring.
// org at center, members close in, projects mid, tech outermost.
const RING_RADIUS = { org: 0, member: 170, project: 320, tech: 460 };
// Tech ring is the densest — collision r=34 covers the diamond + ~30px
// label width. Members + projects are sparser, smaller halos work.
const COLLISION_R = { org: 36, member: 30, project: 28, tech: 34 };

const ZOOM_MIN = 0.3;
const ZOOM_MAX = 3;

const BubbleMap = ({ graph }) => {
  const svgRef = useRef(null);
  const zoomRef = useRef(null); // d3-zoom behavior, kept for buttons
  const viewportRef = useRef(null); // <g> that d3-zoom transforms
  const [tip, setTip] = useState(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    if (!svgRef.current || !graph || !graph.nodes.length) return undefined;

    const width = svgRef.current.clientWidth || 1000;
    const height = 800;
    const cx = width / 2;
    const cy = height / 2;

    // Clone nodes/links so d3 can mutate (x, y, vx, vy, source/target ref)
    // without polluting the React-owned graph object.
    const nodes = graph.nodes.map((n) => ({ ...n }));
    const links = graph.links.map((l) => ({ ...l }));

    const simulation = d3Force
      .forceSimulation(nodes)
      .force(
        "link",
        d3Force
          .forceLink(links)
          .id((d) => d.id)
          .distance(70)
          .strength(0.25),
      )
      .force("charge", d3Force.forceManyBody().strength(-140))
      .force(
        "radial",
        d3Force
          .forceRadial((d) => RING_RADIUS[d.type] || 0, cx, cy)
          .strength(0.9),
      )
      .force(
        "collision",
        d3Force.forceCollide().radius((d) => COLLISION_R[d.type] || 16),
      );

    // Anchor org dead-center so the whole map orbits around it.
    nodes.forEach((n) => {
      if (n.type === "org") {
        n.fx = cx;
        n.fy = cy;
      }
    });

    const svg = d3Sel.select(svgRef.current);
    svg.selectAll("*").remove();

    // Viewport <g> — d3-zoom applies its transform here so wheel/drag/pinch
    // scale + pan rings, links, and nodes together. Node-drag still works
    // because d3-drag fires on the node selection inside the viewport.
    const viewport = svg.append("g").attr("class", "viewport");
    viewportRef.current = viewport.node();

    const zoom = d3Zoom
      .zoom()
      .scaleExtent([ZOOM_MIN, ZOOM_MAX])
      .filter((event) => {
        // Block d3-zoom drag pan when the gesture starts on a node — that
        // belongs to d3-drag. Wheel + background drag still trigger zoom.
        if (event.type !== "wheel" && event.target.closest(".node")) return false;
        return !event.ctrlKey && !event.button;
      })
      .on("zoom", (event) => {
        viewport.attr("transform", event.transform);
        setScale(event.transform.k);
      });
    svg.call(zoom);
    zoomRef.current = zoom;

    // Faint concentric ring guides — visually communicate the 4 layers.
    const ringGroup = viewport.append("g");
    ["member", "project", "tech"].forEach((t) => {
      ringGroup
        .append("circle")
        .attr("cx", cx)
        .attr("cy", cy)
        .attr("r", RING_RADIUS[t])
        .attr("fill", "none")
        .attr("stroke", "var(--nd-border)")
        .attr("stroke-width", 0.5)
        .attr("stroke-dasharray", "2 6");
    });

    // Curved edges via SVG paths (subtle arc, reduces visual crossings).
    const link = viewport
      .append("g")
      .attr("stroke", "var(--nd-border-visible)")
      .attr("stroke-width", 0.8)
      .attr("fill", "none")
      .attr("opacity", 0.6)
      .selectAll("path")
      .data(links)
      .join("path");

    const node = viewport
      .append("g")
      .selectAll("g")
      .data(nodes)
      .join("g")
      .attr("class", "node")
      .style("cursor", "grab")
      .call(
        d3Drag
          .drag()
          .on("start", (event, d) => {
            if (!event.active) simulation.alphaTarget(0.3).restart();
            d.fx = d.x;
            d.fy = d.y;
          })
          .on("drag", (event, d) => {
            d.fx = event.x;
            d.fy = event.y;
          })
          .on("end", (event, d) => {
            if (!event.active) simulation.alphaTarget(0);
            d.fx = null;
            d.fy = null;
          })
      );

    node.each(function nodeShape(d) {
      const sel = d3Sel.select(this);
      if (d.type === "org") {
        sel
          .append("rect")
          .attr("width", 26)
          .attr("height", 26)
          .attr("x", -13)
          .attr("y", -13)
          .attr("rx", 3)
          .attr("fill", "var(--nd-text-display)");
      } else if (d.type === "member") {
        const r = d.seniority === "junior" ? 8 : 12;
        sel
          .append("circle")
          .attr("r", r)
          .attr("fill", "var(--nd-text-display)")
          .attr("opacity", d.seniority === "junior" ? 0.55 : 1);
      } else if (d.type === "project") {
        sel
          .append("circle")
          .attr("r", 14)
          .attr("fill", "none")
          .attr("stroke", "var(--nd-text-secondary)")
          .attr("stroke-width", 1.5);
      } else {
        sel
          .append("rect")
          .attr("width", 12)
          .attr("height", 12)
          .attr("x", -6)
          .attr("y", -6)
          .attr("transform", "rotate(45)")
          .attr("fill", "none")
          .attr("stroke", "var(--nd-accent-subtle)")
          .attr("stroke-width", 1.5);
      }

      const isHeroLabel = d.type === "org" || d.type === "member";
      sel
        .append("text")
        .attr("dy", d.type === "org" ? 30 : 24)
        .attr("text-anchor", "middle")
        .attr(
          "font-family",
          isHeroLabel
            ? "'Space Grotesk', sans-serif"
            : "'Space Mono', monospace"
        )
        .attr("font-size", d.type === "org" ? "14px" : isHeroLabel ? "12px" : "10px")
        .attr(
          "fill",
          d.type === "org" ? "var(--nd-text-display)" : "var(--nd-text-secondary)"
        )
        .text(d.label);
    });

    // Hover tooltip — projects (industry · status) + members (role · seniority)
    const showTip = (event, d) => {
      let body = "";
      if (d.type === "project") body = `${d.label} · ${d.industry} · ${d.status}`;
      else if (d.type === "member") body = `${d.label} · ${d.role || ""}`.trim();
      else return;
      setTip({ x: event.clientX, y: event.clientY, body });
    };
    node
      .filter((d) => d.type === "project" || d.type === "member")
      .on("mouseover", showTip)
      .on("mousemove", (event) => {
        setTip((prev) =>
          prev ? { ...prev, x: event.clientX, y: event.clientY } : prev
        );
      })
      .on("mouseout", () => setTip(null));

    simulation.on("tick", () => {
      link.attr("d", (d) => {
        const sx = d.source.x;
        const sy = d.source.y;
        const tx = d.target.x;
        const ty = d.target.y;
        // Quadratic curve with small perpendicular bow so parallel edges
        // don't overlap. Bow magnitude scales with edge length.
        const dx = tx - sx;
        const dy = ty - sy;
        const len = Math.sqrt(dx * dx + dy * dy) || 1;
        const bow = Math.min(40, len * 0.12);
        const mx = (sx + tx) / 2 - (dy / len) * bow;
        const my = (sy + ty) / 2 + (dx / len) * bow;
        return `M${sx},${sy} Q${mx},${my} ${tx},${ty}`;
      });
      node.attr("transform", (d) => `translate(${d.x}, ${d.y})`);
    });

    return () => {
      simulation.stop();
      setTip(null);
    };
  }, [graph]);

  const zoomBy = (factor) => {
    if (!svgRef.current || !zoomRef.current) return;
    d3Sel.select(svgRef.current).transition().duration(200).call(zoomRef.current.scaleBy, factor);
  };
  const zoomReset = () => {
    if (!svgRef.current || !zoomRef.current) return;
    d3Sel.select(svgRef.current).transition().duration(250).call(zoomRef.current.transform, d3Zoom.zoomIdentity);
  };

  return (
    <Wrap>
      <svg
        ref={svgRef}
        style={{ width: "100%", height: 800, display: "block" }}
        aria-label="Concentric map: Dioscuro center, team inner ring, projects middle, tech outer"
      />
      <ZoomBar role="group" aria-label="Zoom controls">
        <ZoomBtn type="button" aria-label="Zoom in" onClick={() => zoomBy(1.4)}>+</ZoomBtn>
        <ZoomBtn type="button" aria-label="Zoom out" onClick={() => zoomBy(1 / 1.4)}>−</ZoomBtn>
        <ZoomBtn type="button" aria-label="Reset zoom" onClick={zoomReset}>⌂</ZoomBtn>
        <ZoomLevel>{Math.round(scale * 100)}%</ZoomLevel>
      </ZoomBar>
      {tip && (
        <Tooltip style={{ left: tip.x, top: tip.y }}>{tip.body}</Tooltip>
      )}
    </Wrap>
  );
};

export default BubbleMap;
